import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { addMonths } from 'date-fns';
import { Left, Right } from '@linktivity/link-icons';
import { Skeleton } from '@linktivity/link-ui';
import { MonthlyCalendar } from '@link/share/components';
import type { CalendarCellData } from '@link/share/components';
import { ALLOTMENT_STATUS } from '@link/share/constants';
import { useDateFormat } from '@link/share/hooks/useDateFormat';
import StockIcon from '../StockIcon';
import { dayStockStatus, type CalendarDay } from '../stockStatus';
import type { AvailabilityCalendarProps } from './AvailabilityCalendar.types';
import { monthKey, useVisibleMonth } from './useVisibleMonth';
import styles from './availabilityCalendar.module.css';

const WEEKDAY_COUNT = 7;
const DAY_COUNT = 35;

const LEGEND = [
  { status: ALLOTMENT_STATUS.ALLOCABLE, labelKey: 'available' },
  { status: ALLOTMENT_STATUS.LIMITED, labelKey: 'limited' },
  { status: ALLOTMENT_STATUS.SOLD_OUT, labelKey: 'soldOut' }
] as const;

/**
 * A month of days with their stock, for picking a booking date, and the
 * legend for the stock marks under it.
 *
 * Browser only in practice: the days come from the calendar API, which the
 * page calls after hydration.
 */
const AvailabilityCalendar = ({
  days,
  value,
  isDayBookable,
  paging,
  hideFinalSoldOutMonthMarks,
  onSelect
}: AvailabilityCalendarProps) => {
  const { t } = useTranslation();
  const { formatYearMonth } = useDateFormat();
  const visible = useVisibleMonth({ value, days, paging });
  const { month, canGoForward } = visible;

  const dayByDate = useMemo(() => {
    const map = new Map<string, CalendarDay>();
    for (const day of days) if (day.date) map.set(day.date, day);
    return map;
  }, [days]);

  // The last month there is, with nothing left to book in it: its marks would
  // be a page of ×, so it is shown bare.
  const hideMarks = useMemo(() => {
    if (!hideFinalSoldOutMonthMarks || canGoForward || paging.isLoadingMore) {
      return false;
    }
    const key = monthKey(month);
    for (const [date, day] of dayByDate) {
      if (date.startsWith(key) && isDayBookable(day)) return false;
    }
    return true;
  }, [
    hideFinalSoldOutMonthMarks,
    canGoForward,
    paging.isLoadingMore,
    month,
    dayByDate,
    isDayBookable
  ]);

  const renderCell = useCallback(
    ({ date, day: dayOfMonth, isEmpty }: CalendarCellData) => {
      if (isEmpty) return <div className={styles.blank} />;
      const day = dayByDate.get(date);
      const bookable = isDayBookable(day);
      const stock =
        day && !hideMarks ? dayStockStatus(day, bookable) : undefined;
      const selected = date === value;
      return (
        <button
          type="button"
          title={date}
          className={cls(styles.cell, selected && styles.selected)}
          disabled={!bookable}
          aria-pressed={selected}
          onClick={() => onSelect(date)}
        >
          <span className={styles.number}>{dayOfMonth}</span>
          {stock && (
            <StockIcon
              status={stock}
              className={cls(styles.mark, styles[stock])}
            />
          )}
        </button>
      );
    },
    [dayByDate, isDayBookable, hideMarks, value, onSelect]
  );

  return (
    <div className={styles.calendar}>
      <div className={styles.nav}>
        <button
          type="button"
          className={styles.button}
          disabled={!visible.canGoBack}
          aria-label={formatYearMonth(addMonths(month, -1))}
          onClick={visible.goBack}
        >
          <Left className={styles.icon} aria-hidden />
        </button>
        <span className={styles.month}>{formatYearMonth(month)}</span>
        <button
          type="button"
          className={styles.button}
          disabled={!canGoForward}
          aria-label={formatYearMonth(addMonths(month, 1))}
          onClick={visible.goForward}
        >
          <Right className={styles.icon} aria-hidden />
        </button>
      </div>
      {visible.isMonthLoading ? (
        <div className={styles.loading}>
          <div className={styles.loadingWeekdays}>
            {Array.from({ length: WEEKDAY_COUNT }, (_, i) => (
              <Skeleton key={i} className={styles.loadingWeekday} />
            ))}
          </div>
          <div className={styles.loadingDays}>
            {Array.from({ length: DAY_COUNT }, (_, i) => (
              <Skeleton key={i} className={styles.loadingDay} />
            ))}
          </div>
        </div>
      ) : (
        <MonthlyCalendar
          className={styles.grid}
          startDate={month}
          renderCell={renderCell}
        />
      )}
      {/* What ○ / △ / × mean. */}
      <ul className={styles.legend}>
        {LEGEND.map(({ status, labelKey }) => (
          <li key={status} className={styles.entry}>
            <StockIcon
              status={status}
              className={cls(styles.legendMark, styles[status])}
            />
            <span className={styles.label}>
              {t(`components.collapsibleCard.stockStatus.${labelKey}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AvailabilityCalendar;
